/**
 * Page /admin/utilisateurs — gestion des utilisateurs.
 *
 * Liste avec filtres (rôle, audience, recherche), modal détail avec devis
 * récents, actions : promouvoir admin / rétrograder / désactiver / supprimer.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/components/admin/ConfirmProvider";
import {
    Users as UsersIcon, Loader2, Search, Filter, ShieldCheck, UserMinus, Trash2,
    Inbox, FileImage, X, Building2, User as UserIcon, ChevronRight,
    UserPlus, Save,
} from "lucide-react";

function _extractError(e) {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail === "permission_denied" ? "Accès non autorisé" : detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
    if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
    return e?.message || "Erreur inconnue";
}

// --- RBAC équipe (cf. rbac_spec.md §1/§2) -----------------------------------
// 16 clés de permission, source de vérité alignée sur le backend (rbac.py).
const ALL_PERMISSIONS = [
    "analytics", "quotes", "cee_leads", "isolation_leads", "callbacks", "contact_messages",
    "mail", "partner_offers", "suppliers", "tariffs", "clients", "team", "content",
    "notifications", "whatsapp", "audit",
];

const PERMISSION_LABELS = {
    analytics: "Analytics",
    quotes: "Devis",
    cee_leads: "Leads CEE & Solaire",
    isolation_leads: "Leads Isolation/PAC",
    callbacks: "Rappels demandés",
    contact_messages: "Messages contact",
    mail: "Boîte mail",
    partner_offers: "Offres partenaires",
    suppliers: "Fournisseurs",
    tariffs: "Tarifs & marché",
    clients: "Clients",
    team: "Équipe",
    content: "Contenu du site",
    notifications: "Notifications",
    whatsapp: "WhatsApp",
    audit: "Journal d'audit",
};

// Presets de rôle (§2 de la spec) : choisir un rôle applique sa liste de
// permissions ; le backend stocke toujours la liste explicite, admin_role
// n'est qu'un libellé.
const ROLE_PRESETS = {
    super_admin: ALL_PERMISSIONS,
    commercial: ["analytics", "quotes", "cee_leads", "isolation_leads", "callbacks", "contact_messages", "mail", "clients"],
    editeur: ["content", "notifications", "suppliers", "partner_offers", "tariffs"],
};

const ROLE_LABELS = {
    super_admin: "Super admin",
    commercial: "Commercial",
    editeur: "Éditeur",
    custom: "Personnalisé",
};

const ROLE_OPTIONS = [
    { value: "super_admin", label: ROLE_LABELS.super_admin },
    { value: "commercial", label: ROLE_LABELS.commercial },
    { value: "editeur", label: ROLE_LABELS.editeur },
    { value: "custom", label: ROLE_LABELS.custom },
];

/** Libellé de rôle affiché pour un membre de l'équipe (compat legacy : pas de
 * admin_role en base → traité comme super_admin, comme avant le RBAC). */
function roleLabel(u) {
    if (u.role !== "admin") return null;
    const r = u.admin_role || "super_admin";
    return ROLE_LABELS[r] || ROLE_LABELS.custom;
}

/** Deux presets sont égaux si mêmes éléments, ordre indifférent. */
function samePermSet(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    const bs = new Set(b);
    return a.every((p) => bs.has(p));
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}


/**
 * Composant générique pour la gestion d'utilisateurs.
 *
 * Props :
 *   mode = "team"    → équipe interne (admins uniquement)
 *   mode = "clients" → clients du site (role=user, B2C/B2B)
 *   mode = "all"     → tous (legacy, page /admin/utilisateurs)
 */
export default function AdminUsers({ mode = "all" }) {
    const { user: me } = useAuth();
    const confirm = useConfirm();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    // Filter de rôle : forcé selon le mode, ou libre en mode "all"
    const forcedRole = mode === "team" ? "admin" : mode === "clients" ? "user" : null;
    const [roleFilter, setRoleFilter] = useState(forcedRole || "");
    const [audienceFilter, setAudienceFilter] = useState("");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [creating, setCreating] = useState(false);

    // Libellés contextuels selon le mode
    const labels = {
        team: {
            eyebrow: "Réglages",
            title: "Équipe",
            empty: "Aucun administrateur",
            createBtn: "Nouvel administrateur",
            createTitle: "Nouvel administrateur",
            createDefaults: { role: "admin", audience: "particulier" },
            showAudienceFilter: false,
            statsText: (s) => `${s.total} membres de l'équipe${s.disabled > 0 ? ` · ${s.disabled} désactivés` : ""}`,
            description: "Les comptes qui ont accès à l'admin (création/modification de contenu, devis, fournisseurs, etc.).",
            allowRoleToggle: true, // garder pour rétrograder un admin en user
        },
        clients: {
            eyebrow: "Comptes",
            title: "Clients",
            empty: "Aucun client",
            createBtn: "Nouveau client",
            createTitle: "Nouveau client",
            createDefaults: { role: "user", audience: "particulier" },
            showAudienceFilter: true,
            statsText: (s) => `${s.total} clients · ${s.b2c} particuliers · ${s.b2b} entreprises${s.disabled > 0 ? ` · ${s.disabled} désactivés` : ""}`,
            description: "Tes clients inscrits sur le site (particuliers et entreprises). Ils peuvent demander des devis, télécharger leurs factures, etc.",
            allowRoleToggle: false, // pas de promotion admin depuis la page Clients
        },
        all: {
            eyebrow: "Comptes",
            title: "Utilisateurs",
            empty: "Aucun utilisateur",
            createBtn: "Nouvel utilisateur",
            createTitle: "Nouvel utilisateur",
            createDefaults: { role: "user", audience: "particulier" },
            showAudienceFilter: true,
            statsText: (s) => `${s.total} utilisateurs · ${s.admin} admins · ${s.b2c} particuliers · ${s.b2b} entreprises${s.disabled > 0 ? ` · ${s.disabled} désactivés` : ""}`,
            description: null,
            allowRoleToggle: true,
        },
    }[mode];

    async function load() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            // Si mode force un rôle, il prime sur roleFilter UI
            const effectiveRole = forcedRole || roleFilter;
            if (effectiveRole) params.set("role", effectiveRole);
            if (audienceFilter) params.set("audience", audienceFilter);
            if (search.trim()) params.set("search", search.trim());
            const { data } = await api.get(`/admin/users?${params.toString()}`);
            setList(data || []);
        } catch (e) {
            toast.error("Chargement : " + _extractError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter, audienceFilter]);
    // Debounce recherche
    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [search]);

    async function toggleRole(u) {
        const newRole = u.role === "admin" ? "user" : "admin";
        try {
            await api.patch(`/admin/users/${u.id}`, { role: newRole });
            toast.success(newRole === "admin" ? `${u.name || u.email} promu admin` : `${u.name || u.email} rétrogradé user`);
            load();
        } catch (e) {
            toast.error("Échec : " + _extractError(e));
        }
    }

    async function toggleDisabled(u) {
        try {
            await api.patch(`/admin/users/${u.id}`, { disabled: !u.disabled });
            toast.success(u.disabled ? "Réactivé" : "Désactivé");
            load();
        } catch (e) {
            toast.error("Échec : " + _extractError(e));
        }
    }

    async function remove(u) {
        if (!(await confirm({ title: "Supprimer cet utilisateur ?", message: `${u.name || u.email} — cette action est irréversible.`, confirmLabel: "Supprimer" }))) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("Supprimé");
            setSelected(null);
            load();
        } catch (e) {
            toast.error("Suppression : " + _extractError(e));
        }
    }

    async function savePermissions(u, adminRole, permissions) {
        try {
            const { data } = await api.patch(`/admin/users/${u.id}`, { admin_role: adminRole, permissions });
            toast.success("Droits mis à jour");
            load();
            return data;
        } catch (e) {
            toast.error("Droits : " + _extractError(e));
            throw e;
        }
    }

    const stats = {
        total: list.length,
        admin: list.filter((u) => u.role === "admin").length,
        b2c: list.filter((u) => u.audience === "particulier").length,
        b2b: list.filter((u) => u.audience === "entreprise").length,
        disabled: list.filter((u) => u.disabled).length,
    };

    return (
        <div className="em-container py-8">
            <p className="em-eyebrow">{labels.eyebrow}</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">
                {labels.title}
            </h1>
            {labels.description && (
                <p className="text-muted-foreground mt-3 max-w-2xl">{labels.description}</p>
            )}
            <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
                <p className="text-muted-foreground">
                    {labels.statsText(stats)}
                </p>
                <button
                    onClick={() => setCreating(true)}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90"
                >
                    <UserPlus className="h-4 w-4" /> {labels.createBtn}
                </button>
            </div>

            {/* Filtres */}
            <div className="flex flex-wrap items-center gap-3 mt-8">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher (email, nom, société)…"
                        className="pl-10 pr-3 py-2 w-full rounded-full border border-border bg-card text-sm"
                    />
                </div>
                {/* Filtres de rôle uniquement en mode "all" (les modes team/clients forcent déjà) */}
                {mode === "all" && (
                    <>
                        <Pill label="Tous" active={!roleFilter} onClick={() => setRoleFilter("")} />
                        <Pill label="Admins" active={roleFilter === "admin"} onClick={() => setRoleFilter("admin")} />
                        <Pill label="Users" active={roleFilter === "user"} onClick={() => setRoleFilter("user")} />
                        <span className="text-muted-foreground/40">·</span>
                    </>
                )}
                {/* Filtres d'audience seulement si pertinents (clients ou all) */}
                {labels.showAudienceFilter && (
                    <>
                        <Pill label="Tous publics" active={!audienceFilter} onClick={() => setAudienceFilter("")} />
                        <Pill label="Particuliers" active={audienceFilter === "particulier"} onClick={() => setAudienceFilter("particulier")} />
                        <Pill label="Entreprises" active={audienceFilter === "entreprise"} onClick={() => setAudienceFilter("entreprise")} />
                    </>
                )}
            </div>

            {/* Liste */}
            {loading ? (
                <div className="mt-12 flex items-center justify-center text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
                </div>
            ) : list.length === 0 ? (
                <div className="mt-12 rounded-3xl border border-border bg-card p-12 text-center">
                    <UsersIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucun utilisateur ne correspond.</p>
                </div>
            ) : (
                <div className="rounded-3xl border border-border bg-card overflow-hidden mt-6">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-left">
                            <tr className="text-xs font-semibold text-muted-foreground uppercase">
                                <th className="px-4 py-3">Utilisateur</th>
                                <th className="px-4 py-3 hidden md:table-cell">Audience</th>
                                <th className="px-4 py-3 hidden md:table-cell">Rôle</th>
                                <th className="px-4 py-3 hidden lg:table-cell">Devis</th>
                                <th className="px-4 py-3 hidden lg:table-cell">Factures</th>
                                <th className="px-4 py-3 hidden lg:table-cell">Inscrit</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {list.map((u) => (
                                <tr key={u.id} className={`hover:bg-muted/30 cursor-pointer ${u.disabled ? "opacity-50" : ""}`} onClick={() => setSelected(u)}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                                {u.audience === "entreprise" ? <Building2 className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-foreground truncate">{u.name || "—"}</p>
                                                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell text-foreground/80">
                                        {u.audience === "entreprise" ? "Entreprise" : "Particulier"}
                                        {u.company && <p className="text-xs text-muted-foreground">{u.company}</p>}
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell">
                                        {u.role === "admin" ? (
                                            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-primary text-primary-foreground">
                                                <ShieldCheck className="h-3 w-3" /> {mode === "team" ? roleLabel(u) : "Admin"}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-foreground/70">User</span>
                                        )}
                                        {u.disabled && <span className="ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-destructive/10 text-destructive">Désactivé</span>}
                                    </td>
                                    <td className="px-4 py-3 hidden lg:table-cell">{u.quotes_count}</td>
                                    <td className="px-4 py-3 hidden lg:table-cell">{u.bills_count}</td>
                                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{fmtDate(u.created_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <ChevronRight className="h-4 w-4 text-muted-foreground inline" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal détail */}
            {selected && (
                <UserDetailModal
                    userId={selected.id}
                    me={me}
                    mode={mode}
                    onClose={() => setSelected(null)}
                    onAction={{ toggleRole, toggleDisabled, remove, savePermissions }}
                />
            )}

            {/* Modal création */}
            {creating && (
                <UserCreateModal
                    title={labels.createTitle}
                    defaults={labels.createDefaults}
                    lockRole={mode !== "all"}
                    onClose={() => setCreating(false)}
                    onCreated={() => { setCreating(false); load(); }}
                />
            )}
        </div>
    );
}


function UserCreateModal({ title = "Nouvel utilisateur", defaults = {}, lockRole = false, onClose, onCreated }) {
    const [form, setForm] = useState({
        email: "", password: "", name: "",
        audience: defaults.audience || "particulier",
        company: "",
        role: defaults.role || "user",
        disabled: false,
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit(e) {
        e.preventDefault();
        if (!form.email || !form.password || !form.name) {
            toast.error("Email, mot de passe et nom requis");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Mot de passe : au moins 8 caractères");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/users", {
                ...form,
                company: form.company.trim() || null,
            });
            toast.success("Utilisateur créé");
            onCreated && onCreated();
        } catch (e) {
            toast.error("Création : " + _extractError(e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
            <form
                onSubmit={submit}
                className="bg-background w-full max-w-2xl rounded-3xl shadow-2xl my-8 overflow-hidden"
            >
                <header className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] opacity-80">Création</p>
                        <h2 className="font-display text-2xl font-bold mt-1">{title}</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Nom complet" value={form.name} onChange={(v) => set("name", v)} required />
                        <Input label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} required autoComplete="off" />
                        <Input label="Mot de passe (≥ 8 caractères)" type="password" value={form.password} onChange={(v) => set("password", v)} required autoComplete="new-password" />
                        <SelectField
                            label="Audience"
                            value={form.audience}
                            onChange={(v) => set("audience", v)}
                            options={[
                                { value: "particulier", label: "Particulier" },
                                { value: "entreprise", label: "Entreprise" },
                            ]}
                        />
                        {form.audience === "entreprise" && (
                            <Input label="Société" value={form.company} onChange={(v) => set("company", v)} placeholder="(optionnel)" />
                        )}
                        {!lockRole && (
                            <SelectField
                                label="Rôle"
                                value={form.role}
                                onChange={(v) => set("role", v)}
                                options={[
                                    { value: "user", label: "Utilisateur" },
                                    { value: "admin", label: "Administrateur" },
                                ]}
                            />
                        )}
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={form.disabled}
                            onChange={(e) => set("disabled", e.target.checked)}
                            className="h-4 w-4 rounded border-border"
                        />
                        Créer désactivé (compte créé mais ne peut pas se connecter)
                    </label>
                </div>

                <footer className="border-t border-border p-4 flex items-center justify-end gap-2">
                    <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm border border-border hover:bg-muted">
                        Annuler
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Créer
                    </button>
                </footer>
            </form>
        </div>
    );
}


function Input({ label, value, onChange, type = "text", required, autoComplete, placeholder }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                autoComplete={autoComplete}
                placeholder={placeholder}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
        </label>
    );
}


function SelectField({ label, value, onChange, options, disabled }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
            >
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    );
}


function Pill({ label, active, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                active ? "bg-primary text-primary-foreground" : "bg-card border border-border text-foreground/70 hover:border-primary/40"
            }`}
        >
            {label}
        </button>
    );
}


function UserDetailModal({ userId, me, mode, onClose, onAction }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // État local de l'éditeur de droits (rôle + 16 cases à cocher). Réinitialisé
    // dès que le user chargé change (cf. effet ci-dessous).
    const [editRole, setEditRole] = useState("super_admin");
    const [editPerms, setEditPerms] = useState(new Set(ALL_PERMISSIONS));
    const [savingPerms, setSavingPerms] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/users/${userId}`);
                setUser(data);
            } catch (e) {
                toast.error("Détail : " + _extractError(e));
                onClose();
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, [userId]);

    // Pré-remplit l'éditeur de droits dès que le détail est chargé. Compat legacy :
    // un admin sans `permissions` en base est traité comme super_admin (tout coché).
    useEffect(() => {
        if (!user) return;
        setEditRole(user.admin_role || "super_admin");
        setEditPerms(new Set(user.permissions && user.permissions.length ? user.permissions : ALL_PERMISSIONS));
    }, [user]);

    const isSelf = me?.id === userId;
    const showPermsEditor = mode === "team" && user?.role === "admin";

    function selectPreset(newRole) {
        setEditRole(newRole);
        if (ROLE_PRESETS[newRole]) {
            setEditPerms(new Set(ROLE_PRESETS[newRole]));
        }
    }

    function togglePerm(key) {
        setEditPerms((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            // Si les cases s'écartent du preset du rôle courant, on bascule
            // visuellement sur "Personnalisé" (le rôle reste un simple libellé,
            // le backend stocke toujours la liste explicite).
            const nextArr = Array.from(next);
            setEditRole((prevRole) => {
                if (prevRole === "custom") return prevRole;
                const preset = ROLE_PRESETS[prevRole];
                return preset && samePermSet(preset, nextArr) ? prevRole : "custom";
            });
            return next;
        });
    }

    async function savePerms() {
        setSavingPerms(true);
        try {
            const updated = await onAction.savePermissions(user, editRole, Array.from(editPerms));
            if (updated) setUser(updated);
        } catch {
            // toast déjà affiché par savePermissions
        } finally {
            setSavingPerms(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-background w-full max-w-3xl rounded-3xl shadow-2xl my-8 overflow-hidden">
                <header className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] opacity-80">Utilisateur</p>
                        <h2 className="font-display text-2xl font-bold mt-1">{user?.name || user?.email || "Chargement…"}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                {loading || !user ? (
                    <div className="p-12 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
                    </div>
                ) : (
                    <>
                        <div className="p-6 space-y-5">
                            {/* Identité */}
                            <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <Detail label="Email" value={user.email} />
                                <Detail label="Audience" value={user.audience === "entreprise" ? "Entreprise" : "Particulier"} />
                                <Detail label="Rôle" value={user.role === "admin" ? "Administrateur" : "Utilisateur"} />
                                {user.company && <Detail label="Société" value={user.company} />}
                                <Detail label="Inscrit le" value={fmtDate(user.created_at)} />
                                <Detail label="Dernière connexion" value={fmtDate(user.last_login_at)} />
                            </section>

                            {/* Droits d'accès (RBAC) — écran Équipe uniquement */}
                            {showPermsEditor && (
                                <section className="rounded-2xl border border-border p-4 space-y-4">
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                        <p className="em-eyebrow">Droits d'accès</p>
                                        {isSelf && (
                                            <span className="text-xs text-muted-foreground">Tu ne peux pas modifier tes propres droits</span>
                                        )}
                                    </div>
                                    <SelectField
                                        label="Rôle"
                                        value={editRole}
                                        onChange={selectPreset}
                                        options={ROLE_OPTIONS}
                                        disabled={isSelf}
                                    />
                                    <div>
                                        <span className="text-xs font-semibold text-muted-foreground">Sections accessibles</span>
                                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                                            {ALL_PERMISSIONS.map((key) => (
                                                <label key={key} className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={editPerms.has(key)}
                                                        onChange={() => togglePerm(key)}
                                                        disabled={isSelf}
                                                        className="h-4 w-4 rounded border-border"
                                                    />
                                                    {PERMISSION_LABELS[key]}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            onClick={savePerms}
                                            disabled={isSelf || savingPerms}
                                            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                                        >
                                            {savingPerms ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                            Enregistrer les droits
                                        </button>
                                    </div>
                                </section>
                            )}

                            {/* Activité */}
                            <section className="grid grid-cols-2 gap-3">
                                <div className="rounded-2xl border border-border p-4 flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Inbox className="h-5 w-5" /></div>
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Devis envoyés</p>
                                        <p className="font-display text-2xl font-bold">{user.quotes_count}</p>
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-border p-4 flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center"><FileImage className="h-5 w-5" /></div>
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Factures uploadées</p>
                                        <p className="font-display text-2xl font-bold">{user.bills_count}</p>
                                    </div>
                                </div>
                            </section>

                            {/* Devis récents */}
                            {user.recent_quotes?.length > 0 && (
                                <section>
                                    <p className="em-eyebrow mb-3">10 derniers devis</p>
                                    <ul className="rounded-2xl border border-border divide-y divide-border max-h-64 overflow-y-auto">
                                        {user.recent_quotes.map((q) => (
                                            <li key={q.id} className="px-4 py-2 text-sm flex items-center justify-between gap-3">
                                                <span className="truncate">
                                                    <span className="font-medium">{q.energy_type === "gas" ? "Gaz" : "Élec"}</span>
                                                    <span className="text-muted-foreground"> · {q.postal_code || "—"}</span>
                                                </span>
                                                <span className="text-xs text-muted-foreground shrink-0">{fmtDate(q.created_at)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </div>

                        {/* Actions */}
                        <footer className="border-t border-border p-4 flex flex-wrap items-center gap-2 justify-end">
                            <button
                                onClick={() => onAction.toggleRole(user)}
                                disabled={isSelf}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border border-border hover:bg-muted disabled:opacity-40"
                                title={isSelf ? "Tu ne peux pas modifier ton propre rôle" : ""}
                            >
                                <ShieldCheck className="h-4 w-4" />
                                {user.role === "admin" ? "Rétrograder en user" : "Promouvoir admin"}
                            </button>
                            <button
                                onClick={() => onAction.toggleDisabled(user)}
                                disabled={isSelf}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border border-border hover:bg-muted disabled:opacity-40"
                            >
                                <UserMinus className="h-4 w-4" />
                                {user.disabled ? "Réactiver" : "Désactiver"}
                            </button>
                            <button
                                onClick={() => onAction.remove(user)}
                                disabled={isSelf}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-40"
                            >
                                <Trash2 className="h-4 w-4" /> Supprimer
                            </button>
                        </footer>
                    </>
                )}
            </div>
        </div>
    );
}


function Detail({ label, value }) {
    return (
        <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase">{label}</p>
            <p className="text-sm text-foreground mt-1 truncate">{value || "—"}</p>
        </div>
    );
}
